import { useMemo, useState, type ReactElement } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { Badge } from 'app/components/ui/badge'
import DashboardTabs from 'app/components/analytics/DashboardTabs'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { formatDate } from 'app/components/ops-datasets/mockData'
import { cn } from 'app/lib/utils'
import { useEvalsPanel } from './useEvalsPanel'
import { RunDetail } from './RunDetail'
import { TrendsChart } from './TrendsChart'
import { NewRunWizard } from './NewRunWizard'
import { RunProgressBanner } from './RunProgressBanner'
import { useOpsEvalsData } from './OpsEvalsContext'
import { analyzeModel, type ModelAnalysis } from './analysis'
import { isNoiseCheck, noiseMarginFor, runLabels } from './runMeta'
import { VERDICT_LABEL } from './VerdictPanel'
import { formatPoints, type Verdict } from './stats'
import { formatCost } from './pricing'
import type { Run } from './types'

type ModeFilter = 'real' | 'simulated' | 'all'

const VERDICT_PILL: Record<Verdict, string> = {
  improved: `border-green-200 bg-green-50 text-green-800`,
  regressed: `border-red-200 bg-red-50 text-red-800`,
  equivalent: `border-default bg-surface-subtle text-default`,
  identical: `border-default bg-surface-subtle text-default`,
  inconclusive: `border-amber-200 bg-amber-50 text-amber-800`,
  'too-few': `border-amber-200 bg-amber-50 text-amber-800`,
}

export function EvalsPanelComponent(): ReactElement {
  const panel = useEvalsPanel()

  return (
    <>
      <RunProgressBanner />
      {panel.selectedRun ? <RunDetail run={panel.selectedRun} /> : <Overview />}
      {panel.isNewRunOpen && (
        <NewRunWizard
          prefill={panel.prefill}
          running={panel.running}
          onClose={panel.closeNewRun}
          onRun={async (config) => {
            panel.closeNewRun()
            await panel.runAndOpen(config)
          }}
        />
      )}
    </>
  )
}

/** One run as a decision: what it compared, the verdict, and how sure it is. */
function RunRow({ run, analysis, onOpen }: { run: Run; analysis: ModelAnalysis; onOpen: () => void }): ReactElement {
  const labels = runLabels(run)
  const noiseCheck = isNoiseCheck(run)
  const { comparison: c } = analysis
  const simulated = run.execution === `simulated`
  const cost = run.modelResults.reduce((sum, result) => sum + result.cost, 0)

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="grid w-full grid-cols-1 gap-x-4 gap-y-2 px-4 py-3 text-left transition-colors hover:bg-surface-subtle md:grid-cols-[minmax(0,1fr)_12rem_10rem_auto] md:items-center"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                `rounded-full border px-2 py-0.5 text-caption-sm font-medium`,
                noiseCheck ? `border-default bg-surface-subtle text-default` : VERDICT_PILL[c.verdict],
              )}
            >
              {noiseCheck ? `Noise check` : VERDICT_LABEL[c.verdict]}
            </span>
            <span className="truncate text-sm font-medium text-strong">{labels.title}</span>
          </div>
          <div className="mt-1 text-caption-sm text-subtle">
            {run.datasetName} · {c.items} calls · {run.models.join(`, `)} · {formatDate(run.startedAt)}
          </div>
        </div>

        <div className="text-sm tabular-nums">
          {/* Colored only when the verdict backs a direction; a tinted "+3" beside
              "Can't tell yet" would claim what the data does not. */}
          <span
            className={cn(
              `font-medium`,
              c.verdict === `improved` ? `text-green-700` : c.verdict === `regressed` ? `text-red-700` : `text-default`,
            )}
          >
            {formatPoints(c.delta, c.items)} pts
          </span>
          {c.interval && c.verdict !== `identical` && (
            <span className="ml-1 text-caption-sm text-minimal">
              ({formatPoints(c.interval.low, c.items)} to {formatPoints(c.interval.high, c.items)})
            </span>
          )}
        </div>

        <div className="text-caption-sm tabular-nums">
          <span className="text-green-700">{c.improved} better</span>
          <span className="text-minimal"> · </span>
          <span className="text-red-700">{c.regressed} worse</span>
        </div>

        <div className="flex items-center gap-2 md:justify-end">
          {simulated ? <Badge color="amber" label="Simulated" /> : <Badge color="blue" label="Real" />}
          <span className="text-caption-sm tabular-nums text-subtle">{formatCost(cost)}</span>
        </div>
      </button>
    </li>
  )
}

function Overview(): ReactElement {
  const { runs, view, showList, showTrends, openNewRun, openRun, seeding, running } = useEvalsPanel()
  const { versionEvents } = useOpsEvalsData()

  const realCount = runs.filter((run) => run.execution !== `simulated`).length
  const [mode, setMode] = useState<ModeFilter | null>(null)
  // Real runs are the evidence; show them first whenever there are any.
  const activeMode: ModeFilter = mode ?? (realCount > 0 ? `real` : `all`)

  const rows = useMemo(
    () =>
      runs
        .filter((run) =>
          activeMode === `all`
            ? true
            : activeMode === `real`
              ? run.execution !== `simulated`
              : run.execution === `simulated`,
        )
        .map((run) => {
          const result = run.modelResults[0]
          return {
            run,
            analysis: analyzeModel(result, run.stages, run.id, noiseMarginFor(run, result.model, runs).margin),
          }
        }),
    [runs, activeMode],
  )

  const modes: SegmentedOption<ModeFilter>[] = [
    { id: `real`, label: `Real ${realCount}`, title: `Runs on a real model` },
    { id: `simulated`, label: `Simulated ${runs.length - realCount}`, title: `Synthetic scores; not evidence` },
    { id: `all`, label: `All ${runs.length}` },
  ]

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <DashboardTabs
            openTab={view === `trends` ? `Trends` : `Runs`}
            onSetOpenTab={(tab) => (tab === `Trends` ? showTrends() : showList())}
            tabOptions={[`Runs`, `Trends`]}
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          className="mt-1 shrink-0"
          onClick={openNewRun}
          disabled={running}
          title={running ? `A run is in progress` : undefined}
        >
          <PlusIcon className="mr-1 h-4 w-4" /> New run
        </Button>
      </div>

      {seeding ? (
        <TableEmptyState title="Loading runs…" />
      ) : view === `trends` ? (
        <TrendsChart runs={runs} events={versionEvents} onOpenRun={openRun} />
      ) : (
        <section className="mt-6">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-caption-sm text-subtle">
              Each run compares a candidate prompt with a baseline on the same calls. The range is where the true
              difference likely lies.
            </p>
            <SegmentedControl options={modes} value={activeMode} onSelect={setMode} />
          </div>
          {rows.length === 0 ? (
            <TableEmptyState
              title={activeMode === `real` ? `No real runs yet` : `No runs yet`}
              description="Start one with “New run”."
            />
          ) : (
            <ul className="divide-y divide-[var(--line-subtle)] overflow-hidden rounded-lg border border-default bg-surface-raised">
              {rows.map(({ run, analysis }) => (
                <RunRow key={run.id} run={run} analysis={analysis} onOpen={() => openRun(run.id)} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}
